/**
 * WebMCP tool: navigate_to_section
 *
 * Top-level navigation tool registered in App.vue (always mounted).
 * Lets AI agents navigate to major sections of the application without
 * visual scraping — the starting point of any multi-step agent flow.
 *
 * Progressive disclosure hierarchy:
 *   navigate_to_section              ← this tool (App.vue — always available)
 *     └─ navigate_to_organization_tab  (OrganizationPage — available in /organization)
 *          └─ create_*_draft           (tab components — available on correct tab)
 *
 * Usage (in App.vue — Options API setup() method):
 *   import { getCurrentInstance } from "vue";
 *   import { useNavigateToSectionTool } from "@components/webmcp/navigation/useNavigateToSectionTool.js";
 *
 *   setup() {
 *     const { proxy } = getCurrentInstance();
 *     useNavigateToSectionTool({ proxy });
 *     return {};
 *   },
 */

import { onMounted, onBeforeUnmount } from "vue";
import {
  registerTool,
  unregisterTool,
  waitForModelContext,
} from "../core/useWebMcpRegistry.js";
import {
  registerDebugHook,
  unregisterDebugHook,
} from "../core/useWebMcpDebug.js";
import { ORGANIZATION_ROUTE_NAME, ORGANIZATION_TABS } from "./routeConstants.js";
import router from "@/router";

const TOOL_NAME = "navigate_to_section";

/**
 * Valid top-level sections an agent can navigate to.
 * Extend this map when new major sections are added to the app.
 */
const SECTIONS = Object.freeze({
  ORGANIZATION: "organization",
});

export function useNavigateToSectionTool({ proxy }) {
  let debugHandler = null;
  let cancelWait = null;

  const execute = async (input = {}) => {
    const section =
      typeof input.section === "string" ? input.section.trim().toLowerCase() : "";

    if (section !== SECTIONS.ORGANIZATION) {
      return {
        success: false,
        error: `Unknown section "${input.section}". Valid sections: ${Object.values(SECTIONS).join(", ")}.`,
      };
    }

    // Navigating to Organization always lands on Dashboard first.
    // The agent can then call navigate_to_organization_tab to go deeper.
    try {
      await router
        .push({ name: ORGANIZATION_ROUTE_NAME, params: { tab: ORGANIZATION_TABS.DASHBOARD } })
        .catch(() => {});

      return {
        success: true,
        message:
          "Navigated to Organization > Dashboard. " +
          "Call navigate_to_organization_tab to go to a specific tab " +
          `(${Object.values(ORGANIZATION_TABS).join(", ")}).`,
        section: SECTIONS.ORGANIZATION,
        currentTab: ORGANIZATION_TABS.DASHBOARD,
      };
    } catch (error) {
      return {
        success: false,
        error: "Navigation to Organization failed.",
      };
    }
  };

  const toolDefinition = {
    name: TOOL_NAME,
    description:
      "Navigate to a major section of the application. " +
      `Currently supported sections: ${Object.values(SECTIONS).join(", ")}. ` +
      "After navigating to Organization you can call navigate_to_organization_tab " +
      "to reach a specific tab.",
    inputSchema: {
      type: "object",
      properties: {
        section: {
          type: "string",
          description: `The section to navigate to. One of: ${Object.values(SECTIONS).join(", ")}.`,
          enum: Object.values(SECTIONS),
        },
      },
      required: ["section"],
      additionalProperties: false,
    },
    execute: async (input = {}) => execute(input),
    annotations: { readOnlyHint: false },
  };

  onMounted(() => {
    cancelWait = waitForModelContext(() => {
      registerTool(toolDefinition, {
        onWarning: (msg, err) => console.warn(msg, err),
      });
    });
    debugHandler = async (input) => execute(input);
    registerDebugHook(TOOL_NAME, debugHandler);
  });

  onBeforeUnmount(() => {
    cancelWait?.();
    unregisterTool(TOOL_NAME);
    unregisterDebugHook(TOOL_NAME, debugHandler);
    debugHandler = null;
  });

  return { execute };
}
